import { FolderViewOutlined } from "@ant-design/icons";
import { useCustom } from "@refinedev/core";
import { config } from "@src/config";
import { FilterFormInstanceType } from "@src/contexts/ab-testing/ab-context";
import { FilterFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { AbaTicketType, AbaViewItemType } from "@src/types/ab-testing-exploration";
import { parsedFilterFormValueFromApi } from "@src/util/ab-testing/handle-init-value-for-ab-test";
import { processingAbaConfigToForm } from "@src/util/ab-testing/map-view-config-to-form";
import { shared } from "@src/util/shared";
import { Dropdown, Form, Select, Spin, Tooltip } from "antd";
import { FormInstance } from "antd/lib";
import React from "react";

export const AbaViewConfig: React.FC<{ form: FormInstance<FilterFormInstanceType> }> = ({ form }) => {
  const productIdWatch = Form.useWatch(FilterFormFieldsEnum.PMP_PRODUCT_ID, form);
  const abaIdWatch = Form.useWatch(FilterFormFieldsEnum.ABA_ID, form);
  const ticketIdWatch = Form.useWatch(FilterFormFieldsEnum.TICKET_ID, form);

  const isMissRequiredField = !productIdWatch || !ticketIdWatch || !abaIdWatch;

  const {
    data,
    isLoading: isLoadingGetViews,
    isFetching: isFetchingListViews,
  } = useCustom<AbaViewItemType>({
    url: `${config.ABALYZER_URL}/ticket/${ticketIdWatch}/view`,
    method: "get",
    errorNotification: false,
    queryOptions: {
      enabled: !isMissRequiredField,
      retry: 0,
    },
    config: {
      headers: {
        "x-product-id": productIdWatch,
        "X-Goog-IAP-JWT-Assertion": `Bearer ${shared.get("token")}`,
      },
    },
  });
  const {
    data: dataSourceTicket,
    isLoading: isLoadingSourceTicket,
    isFetching: isFetchingSourceTicket,
  } = useCustom<AbaTicketType>({
    url: `${config.ABALYZER_URL}/ticket/${ticketIdWatch}`,
    method: "get",
    queryOptions: {
      enabled: !isMissRequiredField,
      retry: 0,
    },
    config: {
      headers: {
        "x-product-id": productIdWatch,
        "X-Goog-IAP-JWT-Assertion": `Bearer ${shared.get("token")}`,
      },
    },
  });

  const listViews = (data?.data ?? []) as AbaViewItemType[];

  const isListViewLoading =
    !isMissRequiredField &&
    (isLoadingGetViews || isFetchingListViews || isLoadingSourceTicket || isFetchingSourceTicket);
  const isEmptyListViews = listViews?.length === 0;

  const handleSelectView = (viewItem: AbaViewItemType) => {
    const sourceTicket = dataSourceTicket?.data;
    if (sourceTicket && viewItem) {
      const processingConfig = processingAbaConfigToForm(sourceTicket, viewItem);
      const formValues = parsedFilterFormValueFromApi(processingConfig, { form });
      form.setFieldsValue(formValues);
    }
  };
  const selectDropdownRef = React.useRef<any>();
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  return (
    <Spin spinning={isListViewLoading}>
      <Tooltip
        title={
          isMissRequiredField
            ? "Select Aba ID to enable this button"
            : isEmptyListViews
            ? "No views found"
            : "Apply View configuration from Abalyzer"
        }
      >
        <Dropdown
          open={dropdownOpen}
          trigger={["click"]}
          onOpenChange={(open) => {
            if (isMissRequiredField) return;
            setDropdownOpen(open);
            if (open) {
              setTimeout(() => {
                selectDropdownRef.current?.focus();
              }, 200);
            }
          }}
          destroyOnHidden
          popupRender={() => {
            return (
              <Select
                open={dropdownOpen}
                ref={selectDropdownRef}
                style={{
                  width: 250,
                }}
                showSearch
                filterOption={(inputValue, otp) => {
                  return (
                    otp?.origin.name.toLowerCase().includes(inputValue.toLowerCase()) ??
                    otp?.origin.id.toString().toLowerCase().includes(inputValue.toLowerCase()) ??
                    false
                  );
                }}
                onSelect={(_, option) => {
                  handleSelectView(option.origin);
                  setDropdownOpen(false);
                }}
                options={listViews?.map((_view: AbaViewItemType) => {
                  return {
                    key: _view.id,
                    label: (
                      <Tooltip
                        mouseLeaveDelay={0}
                        destroyOnHidden
                        title={
                          <div className="flex flex-col">
                            <span>Name : {_view?.name}</span>
                            <span>ID : {_view?.id}</span>
                          </div>
                        }
                      >
                        <div className="font-semibold">
                          <span>{_view?.name}</span> <span className="font-normal">|</span> <span>{_view?.id}</span>
                        </div>
                      </Tooltip>
                    ),
                    value: _view.id,
                    origin: _view,
                  };
                })}
              />
            );
          }}
        >
          <FolderViewOutlined
            className={`text-[20px] duration-100 ${
              isMissRequiredField
                ? "text-gray-300"
                : isEmptyListViews
                ? "text-gray-500"
                : "text-gray-500 hover:text-gray-800"
            } cursor-pointer`}
          />
        </Dropdown>
      </Tooltip>
    </Spin>
  );
};
